/**
 * Doserne Academy — Admin Kurs Settings
 *
 * Only admins may reach /doserne/admin/kurs. Two panels:
 *  1) Issuer configuration (name, title, signature image) — used on every
 *     certificate PDF as "På vegne av / Din partea / On behalf of".
 *  2) Per-course + per-lesson image editing.  Admin can upload / preview /
 *     replace / remove the cover image at course level, and at lesson level.
 *     Defaults to one image shared by NO/RO/EN; per-language override is
 *     available under the "Per language" toggle.
 */
import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Upload, Trash2, ChevronDown, ChevronRight, Loader2, ImageIcon, Save } from "lucide-react";
import DoserneShell from "./DoserneShell";
import { useDoserneAuth } from "./doserneAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ["default", "no", "ro", "en"];
const LANG_LABELS = { default: "Alle språk (standard)", no: "Norsk", ro: "Română", en: "English" };

export default function DoserneAdminKursSettings() {
  const { user, loading, authHeader } = useDoserneAuth();

  if (loading) return null;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;
  if (!user.is_admin) return <Navigate to="/doserne/kurs" replace />;

  return (
    <DoserneShell>
      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-8" data-testid="admin-kurs-settings">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Kursinnstillinger</h1>
          <p className="text-sm text-slate-500 mt-1">Utsteder for kursbevis + bilder for kurs og leksjoner. Kun for administratorer.</p>
        </div>

        <IssuerPanel authHeader={authHeader} />
        <CoursesPanel authHeader={authHeader} />
      </div>
    </DoserneShell>
  );
}

// ---------------------------------------------------------------------------
// Issuer panel
// ---------------------------------------------------------------------------
function IssuerPanel({ authHeader }) {
  const [form, setForm] = useState({ name: "", title: "", signature_image_url: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [flash, setFlash] = useState("");

  useEffect(() => {
    axios.get(`${API}/doserne/admin/kurs/issuer-config`, authHeader())
      .then(({ data }) => setForm({
        name: data.name || "",
        title: data.title || "",
        signature_image_url: data.signature_image_url || "",
      }))
      .catch(() => {});
  }, [authHeader]);

  const save = async () => {
    setSaving(true); setFlash("");
    try {
      await axios.put(`${API}/doserne/admin/kurs/issuer-config`, form, authHeader());
      setFlash("Lagret ✓");
    } catch { setFlash("Feil ved lagring"); }
    setSaving(false);
    setTimeout(() => setFlash(""), 2500);
  };

  const uploadSignature = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "issuer_signature");
      const { data } = await axios.post(`${API}/doserne/admin/kurs/upload-image`, fd, authHeader());
      setForm((f) => ({ ...f, signature_image_url: data.url }));
    } catch { /* ignore */ }
    setUploading(false);
  };

  return (
    <section className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
      <h2 className="text-lg font-black text-slate-900">Utsteder på kursbevis</h2>
      <p className="text-sm text-slate-500 mb-5">Vises som &quot;På vegne av / Din partea / On behalf of&quot; på hvert bevis. Adressen (Brobekkveien 100, Oslo) og org.nr. 937 207 050 er hardkodet.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Navn" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="issuer-name" />
        <Field label="Tittel" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} testid="issuer-title" />
      </div>
      <div className="mt-4">
        <label className="block text-xs font-bold uppercase tracking-[0.25em] text-slate-500 mb-2">Signaturbilde (PNG med transparent bakgrunn anbefalt)</label>
        <div className="flex items-center gap-4 flex-wrap">
          {form.signature_image_url ? (
            <img src={form.signature_image_url} alt="signatur" className="h-14 max-w-[220px] object-contain bg-slate-50 rounded-lg p-2 border border-slate-200" data-testid="issuer-signature-preview" />
          ) : (
            <div className="h-14 w-48 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">Ingen signatur</div>
          )}
          <label className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold cursor-pointer" data-testid="issuer-signature-upload">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Last opp
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadSignature(e.target.files[0])} />
          </label>
          {form.signature_image_url && (
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, signature_image_url: "" }))}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-bold"
              data-testid="issuer-signature-clear"
            >
              <Trash2 className="w-4 h-4" /> Fjern
            </button>
          )}
        </div>
      </div>
      <div className="mt-6 flex items-center gap-3">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 text-white text-sm font-black" data-testid="issuer-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Lagre utstederinnstillinger
        </button>
        {flash && <span className="text-xs font-bold text-emerald-600">{flash}</span>}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Courses + lessons panel
// ---------------------------------------------------------------------------
function CoursesPanel({ authHeader }) {
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState({});

  useEffect(() => {
    axios.get(`${API}/doserne/kurs`, authHeader())
      .then(({ data }) => setCourses(data.courses || []))
      .catch(() => setCourses([]));
  }, [authHeader]);

  return (
    <section className="rounded-3xl bg-white border border-slate-200 p-6 shadow-sm">
      <h2 className="text-lg font-black text-slate-900">Kurs- og leksjonsbilder</h2>
      <p className="text-sm text-slate-500 mb-5">Last opp / erstatt / fjern coveret for hvert kurs og bildet for hver leksjon. Ingen kodeendring nødvendig.</p>
      <div className="space-y-4">
        {courses.map((c) => (
          <CourseRow key={c.code} course={c} open={!!open[c.code]} onToggle={() => setOpen((s) => ({ ...s, [c.code]: !s[c.code] }))} authHeader={authHeader} />
        ))}
      </div>
    </section>
  );
}

function CourseRow({ course, open, onToggle, authHeader }) {
  const [override, setOverride] = useState(null);
  const [duration, setDuration] = useState(String(course.duration_min || ""));
  const [savingDur, setSavingDur] = useState(false);
  const [lessons, setLessons] = useState(null);

  useEffect(() => {
    axios.get(`${API}/doserne/admin/kurs/${course.code}/override`, authHeader())
      .then(({ data }) => {
        setOverride(data || {});
        if (data?.duration_min != null) setDuration(String(data.duration_min));
      })
      .catch(() => setOverride({}));
  }, [course.code, authHeader]);

  const loadLessons = async () => {
    if (lessons !== null) return;
    try {
      const { data } = await axios.get(`${API}/doserne/kurs/${course.code}`, authHeader());
      setLessons(data.lessons || []);
    } catch { setLessons([]); }
  };

  useEffect(() => { if (open) loadLessons(); }, [open]);

  const saveDuration = async () => {
    setSavingDur(true);
    try {
      const mins = parseInt(duration, 10);
      await axios.put(`${API}/doserne/admin/kurs/${course.code}/override`, { duration_min: isNaN(mins) ? 0 : mins }, authHeader());
    } catch { /* ignore */ }
    setSavingDur(false);
  };

  const title = typeof course.title === "object" ? (course.title.no || course.title.en || Object.values(course.title)[0]) : course.title;

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50" data-testid="course-row">
      <div className="flex items-center gap-3 p-4">
        <button onClick={onToggle} className="text-slate-400 hover:text-slate-900" data-testid="course-row-toggle">
          {open ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black text-slate-900 truncate">{title}</div>
          <div className="text-[10px] font-mono text-slate-500">{course.code} · v{course.version || "—"}</div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            onBlur={saveDuration}
            className="w-20 px-2 py-1 border border-slate-300 rounded-lg text-sm text-right"
            placeholder="min"
            data-testid="course-duration-input"
          />
          <span className="text-xs text-slate-500">min</span>
          {savingDur && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>
      </div>
      {open && (
        <div className="border-t border-slate-200 p-4 space-y-6">
          <ImageEditor
            testid={`course-image-${course.code}`}
            label="Kursbanner"
            baseImage={course.hero_image}
            override={override || {}}
            onChange={(patch) => {
              // Optimistic apply
              setOverride({ ...(override || {}), ...patch });
              const cleanedPatch = {};
              Object.entries(patch).forEach(([k, v]) => { cleanedPatch[k] = v == null ? "" : v; });
              axios.put(`${API}/doserne/admin/kurs/${course.code}/override`, cleanedPatch, authHeader()).catch(() => {});
            }}
            uploadFolder="courses"
            authHeader={authHeader}
          />
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-3">Leksjoner</div>
            {lessons === null && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
            {lessons && lessons.length === 0 && <div className="text-sm text-slate-500">Ingen leksjoner</div>}
            {lessons && lessons.map((l) => (
              <LessonRow key={l.id} lesson={l} authHeader={authHeader} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function LessonRow({ lesson, authHeader }) {
  const [override, setOverride] = useState(null);
  useEffect(() => {
    axios.get(`${API}/doserne/admin/kurs/lessons/${lesson.id}/override`, authHeader())
      .then(({ data }) => setOverride(data || {}))
      .catch(() => setOverride({}));
  }, [lesson.id, authHeader]);

  const title = typeof lesson.title === "object" ? (lesson.title.no || lesson.title.en || Object.values(lesson.title)[0]) : lesson.title;
  return (
    <div className="mt-3 first:mt-0 p-3 rounded-xl bg-white border border-slate-200" data-testid="lesson-row">
      <div className="text-sm font-bold text-slate-900">L{lesson.order} · {title}</div>
      <div className="mt-2">
        <ImageEditor
          testid={`lesson-image-${lesson.id}`}
          label=""
          baseImage={lesson.hero_image}
          override={override || {}}
          onChange={(patch) => {
            setOverride({ ...(override || {}), ...patch });
            const cleanedPatch = {};
            Object.entries(patch).forEach(([k, v]) => { cleanedPatch[k] = v == null ? "" : v; });
            axios.put(`${API}/doserne/admin/kurs/lessons/${lesson.id}/override`, cleanedPatch, authHeader()).catch(() => {});
          }}
          uploadFolder="lessons"
          authHeader={authHeader}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ImageEditor — reusable upload/preview/clear widget with optional per-lang
// ---------------------------------------------------------------------------
function ImageEditor({ label, baseImage, override, onChange, uploadFolder, authHeader, testid }) {
  const [perLang, setPerLang] = useState(false);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    // If any per-lang key is set, auto-expand the per-language view
    if (override.hero_image_no || override.hero_image_ro || override.hero_image_en) setPerLang(true);
  }, [override]);

  const upload = async (langKey, file) => {
    if (!file) return;
    setUploading((s) => ({ ...s, [langKey]: true }));
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", uploadFolder);
      const { data } = await axios.post(`${API}/doserne/admin/kurs/upload-image`, fd, authHeader());
      onChange({ [langKey]: data.url });
    } catch { /* ignore */ }
    setUploading((s) => ({ ...s, [langKey]: false }));
  };

  const renderSlot = (langKey, langLabel) => {
    const url = override[langKey] || (langKey === "hero_image" ? baseImage : "");
    const isDefault = langKey === "hero_image";
    return (
      <div key={langKey} className="flex items-center gap-3 flex-wrap" data-testid={`${testid}-${langKey}`}>
        {url ? (
          <img src={url} alt="preview" className="h-16 w-32 object-cover rounded-lg border border-slate-200 bg-slate-100" />
        ) : (
          <div className="h-16 w-32 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 text-xs">
            <ImageIcon className="w-4 h-4" />
          </div>
        )}
        <div className="text-xs font-bold text-slate-500 min-w-[110px]">{langLabel}</div>
        <label className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold cursor-pointer">
          {uploading[langKey] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {isDefault && !override[langKey] && baseImage ? "Erstatt" : "Last opp"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(langKey, e.target.files[0])} />
        </label>
        {override[langKey] && (
          <button onClick={() => onChange({ [langKey]: "" })} className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold">
            <Trash2 className="w-3.5 h-3.5" /> Fjern
          </button>
        )}
      </div>
    );
  };

  return (
    <div>
      {label && <div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-2">{label}</div>}
      <div className="space-y-2">
        {renderSlot("hero_image", perLang ? "Standard (alle språk)" : LANG_LABELS.default)}
        {perLang && (
          <>
            {renderSlot("hero_image_no", LANG_LABELS.no)}
            {renderSlot("hero_image_ro", LANG_LABELS.ro)}
            {renderSlot("hero_image_en", LANG_LABELS.en)}
          </>
        )}
      </div>
      <button
        type="button"
        onClick={() => setPerLang((v) => !v)}
        className="mt-2 text-xs font-bold text-orange-600 hover:text-orange-700"
      >
        {perLang ? "Bruk samme bilde for alle språk" : "Sett ulikt bilde per språk"}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-1">{label}</span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2 rounded-xl border border-slate-300 focus:border-orange-500 focus:ring-orange-500 outline-none text-sm"
      />
    </label>
  );
}
